import React, { useState, useEffect, useRef } from "react";
import ReactTooltip from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import LoadingPageSm from "../../CommonComponent/LoadingPageSm";
import { Button, Modal } from "react-bootstrap";
import InputLebelComponent from "../../InputLebel/InputLebelComponent";
import { AlertDanger, AlertSuccess } from "../../Alerts/Alert";
import IconContainer from "../../Iconcontainer/IconContainer";
import saveUploadedDocument from "../../../apis/saveUploadedDocument";
import editDocument from "../../../apis/editDocument";
import removeDocument from "../../../apis/removeDocument";
import Swal from "sweetalert2";
import swalWithBootstrapButtons from "sweetalert2-react-content";

const DocumentBoxV2 = ({ documentDetails, resourceSlug }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [showDocumentEditModal, setShowDocumentEditModal] = useState(false);
  const [documentEditId, setDocumentEditId] = useState(null);
  const [documentArray, setDocumentArray] = useState([]);
  const [documentTitle, setDocumentTitle] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [buttonText, setButtonText] = useState("Upload Document");
  const [buttonTextEditDocument, setButtonTextEditDocument] =
    useState("Edit Document");
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [isValidSubmit, setIsValidSubmit] = useState(false);
  const [errorMessage, setErrorMessage] = useState([]);
  const [successMessage, setSuccessMessage] = useState([]);

  const statusRef = useRef(null);
  const executeScroll = () => statusRef.current.scrollIntoView();

  const MySwal = swalWithBootstrapButtons(Swal);

  useEffect(() => {
    setIsLoading(false);
    setDocumentArray(documentDetails || []);
  }, [documentDetails]);

  const resetForm = () => {
    setDocumentTitle("");
    setUploadedFile(null);
    setHasSubmitError(false);
    setIsValidSubmit(false);
    setErrorMessage([]);
    setSuccessMessage([]);
    setIsButtonDisabled(false);
    setButtonText("Upload Document");
    setButtonTextEditDocument("Edit Document");
  };

  const handleClose = () => {
    setShow(false);
    resetForm();
  };

  const handleCloseDocumentModal = () => {
    setShowDocumentEditModal(false);
    resetForm();
  };

  const handleShowDocumentEdit = (id, name) => {
    setDocumentTitle(name);
    setDocumentEditId(id);
    setShowDocumentEditModal(true);
  };

  const handleDocument = (event) => {
    const fileUploaded = event.target.files[0];
    let isInvalid = false;
    let errMessage = [];

    const allowedFormats = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/msword",
    ];

    if (fileUploaded.size > 3000000) {
      isInvalid = true;
      errMessage.push("Document size should be less than 3MB.");
    }

    if (!allowedFormats.includes(fileUploaded.type)) {
      isInvalid = true;
      errMessage.push("Please upload a valid document format.");
    }

    if (isInvalid) {
      executeScroll();
      setErrorMessage(errMessage);
      setHasSubmitError(true);
      setUploadedFile(null);
      event.target.value = null;
    } else {
      setUploadedFile(fileUploaded);
    }
  };

  const handleFormSubmit = () => {
    setIsButtonDisabled(true);
    setButtonText("Processing, please wait...");
    setErrorMessage([]);
    setSuccessMessage([]);
    setHasSubmitError(false);
    setIsValidSubmit(false);

    let errors = [];
    if (!documentTitle.trim()) errors.push("Enter document name");
    if (!uploadedFile) errors.push("Upload a document");

    if (errors.length) {
      setErrorMessage(errors);
      setHasSubmitError(true);
      setIsButtonDisabled(false);
      setButtonText("Upload Document");
      executeScroll();
      return;
    }

    saveUploadedDocument({ resourceSlug, documentTitle, file: uploadedFile })
      .then((res) => {
        const { status, message } = res?.data || {};
        if (status === 1) {
          setSuccessMessage([message]);
          setIsValidSubmit(true);
          setTimeout(() => {
            setShow(false);
            resetForm();
            window.location.reload(false);
          }, 1000);
        } else {
          setErrorMessage([message || "Failed to save document."]);
          setHasSubmitError(true);
        }
      })
      .catch(() => {
        setErrorMessage(["Network error occurred."]);
        setHasSubmitError(true);
      })
      .finally(() => {
        setIsButtonDisabled(false);
        setButtonText("Upload Document");
        executeScroll();
      });
  };

  const handleFormSubmitDocumentEdit = () => {
    if (!documentTitle.trim()) {
      setErrorMessage(["Enter document name"]);
      setHasSubmitError(true);
      executeScroll();
      return;
    }

    setIsButtonDisabled(true);
    setButtonTextEditDocument("Processing, please wait...");

    editDocument({ resourceSlug, documentTitle, documentId: documentEditId })
      .then((res) => {
        const { status, message } = res?.data || {};
        if (status === 1) {
          setSuccessMessage([message]);
          setIsValidSubmit(true);
          setTimeout(() => {
            setShowDocumentEditModal(false);
            resetForm();
            window.location.reload(false);
          }, 1000);
        } else {
          setErrorMessage([message || "Error editing document"]);
          setHasSubmitError(true);
        }
      })
      .catch(() => {
        setErrorMessage(["Network error occurred."]);
        setHasSubmitError(true);
      })
      .finally(() => {
        setIsButtonDisabled(false);
        setButtonTextEditDocument("Edit Document");
        executeScroll();
      });
  };

  const handleRemoveDocument = (id) => {
    MySwal.fire({
      title: "Are you sure?",
      text: "This will permanently remove the document.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, remove it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        removeDocument({ resourceSlug, id })
          .then((res) => {
            if (res?.data?.status === 1) {
              setDocumentArray(
                documentArray.filter((doc) => doc.document_id !== id)
              );
            } else {
              MySwal.fire(
                "Error",
                res?.data?.message || "Failed to delete document",
                "error"
              );
            }
          })
          .catch(() => {
            MySwal.fire("Error", "Network error occurred", "error");
          });
      }
    });
  };

  const displayDocumentTitle = (title = "") =>
    title.length > 20
      ? `${title.substring(0, 10)}...${title.slice(-6)}`
      : title;

  const renderDocuments = () => {
    if (!documentArray?.length)
      return <span className="text-muted">Nothing to display here!</span>;

    return documentArray.map((item, idx) => (
      <div
        className="d-flex col-12 py-1 rounded my-1 bg-light p-1"
        key={item.document_id}
      >
        <div className="col-8 d-flex align-items-center">
          <small className="text-muted" data-tip data-for={`docTip${idx}`}>
            {displayDocumentTitle(item.document_title)}
          </small>
          <ReactTooltip id={`docTip${idx}`}>{item.document_title}</ReactTooltip>
        </div>
        <div className="col-4 d-flex justify-content-end gap-2">
          <div
            className="icon-holder-sm icon-holder-success cursor-pointer"
            onClick={() =>
              handleShowDocumentEdit(item.document_id, item.document_title)
            }
          >
            <IconContainer iconName="FiEdit" />
          </div>
          <div
            className="icon-holder-sm icon-holder-danger cursor-pointer"
            onClick={() => handleRemoveDocument(item.document_id)}
          >
            <IconContainer iconName="FiTrash" />
          </div>
        </div>
      </div>
    ));
  };

  const displayStatusMessage = () => (
    <div className="d-block mt-3" ref={statusRef}>
      {hasSubmitError && <AlertDanger title="Oops" message={errorMessage} />}
      {isValidSubmit && (
        <AlertSuccess title="Success" message={successMessage} />
      )}
    </div>
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Files / Documents</h6>
        <FontAwesomeIcon
          icon={faPlus}
          className="text-primary cursor-pointer"
          onClick={() => setShow(true)}
        />
      </div>
      {isLoading ? <LoadingPageSm /> : renderDocuments()}

      {/* Upload Document Modal */}
      <Modal show={show} onHide={handleClose} centered backdrop="static">
        <Modal.Header>
          <Modal.Title>Upload Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputLebelComponent title="Document Name" />
          <input
            type="text"
            className="form-control mb-3"
            placeholder="Enter document name"
            value={documentTitle}
            onChange={(e) => setDocumentTitle(e.target.value)}
          />
          <InputLebelComponent title="Browse Document" />
          <input
            type="file"
            className="form-control mb-3"
            accept=".pdf,.doc,.docx,.xlsx"
            onChange={handleDocument}
          />
          <div className="text-muted small mb-3">
            Allowed formats: .pdf, .doc, .docx, .xlsx — Max size: 3MB
          </div>
          {displayStatusMessage()}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isButtonDisabled}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleFormSubmit}
            disabled={isButtonDisabled}
          >
            {buttonText}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Document Modal */}
      <Modal
        show={showDocumentEditModal}
        onHide={handleCloseDocumentModal}
        centered
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>Edit Document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputLebelComponent title="Document Name" />
          <input
            type="text"
            className="form-control mb-3"
            placeholder="Edit document name"
            value={documentTitle}
            onChange={(e) => setDocumentTitle(e.target.value)}
          />
          {displayStatusMessage()}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCloseDocumentModal}
            disabled={isButtonDisabled}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleFormSubmitDocumentEdit}
            disabled={isButtonDisabled}
          >
            {buttonTextEditDocument}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DocumentBoxV2;
